import React from 'react';
import { Loader2, ClipboardList } from 'lucide-react';
import { moduleApi } from '../../lib/api';

interface SurveyRequest {
    id: string;
    template_name: string;
    first_name?: string;
    last_name?: string;
    employe_email?: string;
    status: 'pending' | 'responded' | 'completed';
    response_text: string;
    custom_answers: { id: string; question: string; type: 'text' | 'select'; answer: string }[];
    sent_at: string;
    responded_at: string | null;
}

const STATUS_LABELS: Record<string, { label: string; cls: string }> = {
    pending: { label: 'En attente de réponse', cls: 'bg-warning-500/10 text-warning-500 border-warning-500/20' },
    responded: { label: 'Répondu', cls: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
    completed: { label: 'Répondu', cls: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
};

export const EmployeeSurveyRequestsSettings: React.FC = () => {
    const [requests, setRequests] = React.useState<SurveyRequest[]>([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        moduleApi.getEmployeeSurveys().then((d: any) => setRequests(Array.isArray(d) ? d : [])).catch(() => setRequests([])).finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    return (
        <div className="max-w-3xl space-y-4">
            <p className="text-xs text-content-muted italic">Tous les sondages envoyés à vos salariés, tous modèles confondus.</p>

            {requests.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-2xl">
                    <ClipboardList size={28} className="mx-auto mb-2 text-content-muted opacity-40" />
                    <p className="text-content-muted text-sm">Aucun sondage envoyé pour l'instant.</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {requests.map((r) => {
                        const status = STATUS_LABELS[r.status] || STATUS_LABELS.pending;
                        return (
                            <div key={r.id} className="p-4 rounded-2xl bg-surface-soft border border-border space-y-2">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="font-bold text-content text-sm">{`${r.first_name || ''} ${r.last_name || ''}`.trim() || 'Salarié'} <span className="text-content-muted font-normal">— {r.template_name}</span></p>
                                        <p className="text-[11px] text-content-muted mt-0.5">Envoyé le {new Date(r.sent_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                                    </div>
                                    <span className={`shrink-0 text-[10px] font-black uppercase px-2 py-1 rounded-lg border ${status.cls}`}>{status.label}</span>
                                </div>
                                {(r.response_text || (r.custom_answers && r.custom_answers.length > 0)) && (
                                    <div className="text-xs text-content bg-surface rounded-xl p-3 border border-border space-y-1">
                                        {r.response_text && <p><span className="text-content-muted">Réponse :</span> {r.response_text}</p>}
                                        {(r.custom_answers || []).map((a) => (
                                            <p key={a.id}><span className="text-content-muted">{a.question} :</span> {a.answer}</p>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
